/* eslint-disable ember/no-computed-properties-in-native-classes, ember/require-computed-property-dependencies */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import PostStatus from '@linc-technologies/linced-common/enums/post-status';
import moment from 'moment';
import { service } from '@ember/service';
import { sort } from '@ember/object/computed';
import { byKeys, uniq } from '@linc-technologies/linced-common/utils/arrays';

function addChips(posts, entity, chipArray) {
  if (posts.length) {
    let published = posts.filter(
      (item) => item.status === PostStatus.PUBLISHED,
    ).length;
    let unpublished = posts.length - published;
    if (published > 0) {
      chipArray.push({
        entity,
        published: true,
        count: published,
      });
    }
    if (unpublished > 0) {
      chipArray.push({
        entity,
        published: false,
        count: unpublished,
      });
    }
  }
}
export default class ContentController extends Controller {
  @action
  setPostCountType(value) {
    this.postCountType = value;
  }

  @service
  dates;

  learnersSortOrder = Object.freeze(['firstName', 'lastName']);

  @sort('model.learners', 'learnersSortOrder')
  sortedLearners;

  // postsByPage is used to generate pie charts ("Number of Students with Posts per Page").
  @computed('model.{postMeta.[],learners.[]}')
  get postsByPage() {
    let learners = this.model.learners;
    let pages = this.model.pages
      .filter((item) => item.hasPosts)
      .sort(
        (a, b) =>
          a.sequence - b.sequence ||
          (a.label || '').localeCompare(b.label || ''),
      );
    let data = [];

    pages.forEach((page) => {
      let published = [];
      let unpublished = [];
      let noPosts = [];

      learners.forEach((learner) => {
        // Find all posts for this learner + page
        let learnerPosts = this.model.postMeta
          .filter((item) => item.person_id === learner.id)
          .filter((item) => item.page_id === page.id);
        if (learnerPosts.length) {
          if (
            isEmpty(
              learnerPosts.find((item) => item.status === PostStatus.PUBLISHED),
            )
          ) {
            // No published posts, but we have (unpublished) posts.
            unpublished.push(learner.person);
          } else {
            // At least one published post.
            published.push(learner.person);
          }
        } else {
          noPosts.push(learner.person);
        }
      });

      data.push({
        page,
        data: [
          {
            value: 'published',
            label: 'Published',
            count: published.length,
            tooltip: 'Published: ' + published.length,
            pageLabel: page.label,
            learners: published,
          },
          {
            value: 'unpublished',
            label: 'Unpublished',
            count: unpublished.length,
            tooltip: 'Unpublished: ' + unpublished.length,
            pageLabel: page.label,
            learners: unpublished,
          },
          {
            value: 'noposts',
            label: 'No posts',
            count: noPosts.length,
            tooltip: 'No posts: ' + noPosts.length,
            pageLabel: page.label,
            learners: noPosts,
          },
        ],
      });
    });

    return data;
  }

  // Post counts ("Posts Overview") can be by month or author, and counted by Page or by tag from a selected category.
  @tracked
  postCountType = 'month';

  @tracked countBy = 'pages';

  @tracked
  detailDialogHeading;

  @tracked
  detailDialogOpen;

  @tracked
  detailLearners;

  @computed('model.tagCategories.[]')
  get countByOptions() {
    let options = [
      {
        icon: 'stack',
        label: 'Pages',
        value: 'pages',
      },
    ];
    this.model.tagCategories
      .slice()
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''))
      .forEach((category) => {
        options.push({
          icon: 'label',
          label: category.label,
          value: category.id,
        });
      });

    return options;
  }

  @computed('model.pages.@each.hasPosts')
  get filteredPages() {
    return this.model.pages
      .filter((item) => item.hasPosts)
      .sort(byKeys('label'));
  }

  @computed('model.postMeta.[]')
  get postTags() {
    let tags = [];

    this.model.postMeta.forEach((post) => {
      post.tags.forEach((tag) => {
        tags.push(tag);
      });
    });

    return uniq(tags);
  }

  @computed('postTags.[]', 'countBy')
  get filteredTags() {
    if (this.countBy === 'pages') {
      // Not counting by tag.
      return [];
    }
    return this.postTags.filter(
      (item) => item.get('category.id') === this.countBy,
    );
  }

  @computed('dates.rolloverDate')
  get postsByMonthHeaders() {
    let rolloverDate = moment(this.dates.rolloverDate);
    let headers = [];
    let dateFormat = 'MMM YYYY';
    if (rolloverDate.month() === 0 && rolloverDate.date() === 1) {
      dateFormat = 'MMMM';
    }
    for (let i = 0; i < 12; i++) {
      headers.push(rolloverDate.format(dateFormat));
      rolloverDate.add(1, 'month');
    }
    return headers;
  }

  @computed(
    'model.postMeta.[]',
    'sortedLearners.[]',
    'countBy',
    'dates.rolloverDate',
  )
  get postsByMonth() {
    let data = [];

    this.sortedLearners.forEach((learner) => {
      // Find all of this learner's posts.
      let learnerPosts = this.model.postMeta.filter(
        (item) => item.person_id === learner.id,
      );
      let learnerData = [];
      let startDate = moment(this.dates.rolloverDate).date(1);
      for (let i = 0; i < 12; i++) {
        let monthData = [];
        if (learnerPosts.length > 0) {
          // This learner has posts; find the ones for this month.
          let endDate = moment(startDate).add(1, 'month').subtract(1, 'days');
          let monthPosts = learnerPosts.filter((post) => {
            let postDate = moment(post.date * 1000); // Javascript works in ms, not seconds.
            return (
              postDate.isSameOrAfter(startDate, 'day') &&
              postDate.isSameOrBefore(endDate, 'day')
            );
          });

          if (monthPosts.length > 0) {
            // The learner has posts in this month; count up the published/unpublished posts for each page or tag.
            if (this.countBy === 'pages') {
              this.filteredPages.forEach((page) => {
                let pagePosts = monthPosts.filter(
                  (item) => item.page_id === page.id,
                );
                addChips(pagePosts, page, monthData);
              });
            } else {
              // Count by tag
              this.filteredTags.forEach((tag) => {
                let tagPosts = monthPosts.filter((post) => {
                  // tags is an async hasMany; compare ids directly instead.
                  return post.hasMany('tagIds').ids().includes(tag.id);
                });
                addChips(tagPosts, tag, monthData);
              });
            }
          }
        }
        learnerData.push(monthData);
        startDate.add(1, 'month');
      }

      data.push({
        person: learner,
        data: learnerData,
      });
    });

    return data;
  }

  @computed('model.{pages.[],postMeta.[]}')
  get postAuthors() {
    let visiblePageIds = this.model.pages.map((item) => item.id);
    let visiblePosts = this.model.postMeta.filter((meta) => {
      return visiblePageIds.includes(meta.get('page.id'));
    });

    // TODO: Replace the filter() with a filterBy('type', PersonType.STAFF) when we don't need to see LINC-ED Staff.
    return [
      ...new Map(visiblePosts.map((item) => [item.author_id, item])).values(),
    ]
      .map((item) => item.author)
      .filter((person) => {
        let type = person.get('type');
        return type === PersonType.STAFF || type === PersonType.LINC_ED;
      })
      .sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));
  }

  @computed(
    'postAuthors.@each.id',
    'model.postMeta.[]',
    'sortedLearners.[]',
    'countBy',
  )
  get postsByAuthor() {
    let pages = this.model.pages
      .filter((item) => item.hasPosts)
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
    let data = [];

    this.sortedLearners.forEach((learner) => {
      // Find all of this learner's posts.
      let learnerPosts = this.model.postMeta.filter(
        (item) => item.person_id === learner.id,
      );
      let learnerData = [];

      this.postAuthors.forEach((author) => {
        let authorData = [];
        if (learnerPosts.length > 0) {
          // This learner has posts; find the ones by this author.
          let authorPosts = learnerPosts.filter(
            (item) => item.author_id === author.get('id'),
          );

          if (authorPosts.length > 0) {
            // The learner has posts by this author; count up the published/unpublished posts for each page or tag.
            if (this.countBy === 'pages') {
              pages.forEach((page) => {
                let pagePosts = authorPosts.filter(
                  (item) => item.page_id === page.id,
                );
                addChips(pagePosts, page, authorData);
              });
            } else {
              // Count by tag
              this.filteredTags.forEach((tag) => {
                let tagPosts = authorPosts.filter((post) => {
                  // tags is an async hasMany; compare ids directly instead.
                  return post.hasMany('tagIds').ids().includes(tag.id);
                });
                addChips(tagPosts, tag, authorData);
              });
            }
          }
        }

        learnerData.push(authorData);
      });

      data.push({
        person: learner,
        data: learnerData,
      });
    });

    return data;
  }

  @action
  openDetail(slice) {
    this.detailLearners = slice.learners;
    this.detailDialogHeading = `${slice.pageLabel} - ${slice.label}`;
    this.detailDialogOpen = true;
  }

  @action
  setCountBy(option) {
    this.countBy = option.value;
  }
}
